import { tableHeaderStyles } from "../../theme/tableStyles";
import GridPagination from "./GridPagination";
import { Fragment, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import FilterBar from "./FilterBar";
import EmptyState from "./EmptyState";
import ErrorState from "./ErrorState";
import TableSkeleton from "./TableSkeleton";
import TablePagination from "./TablePagination";
import ConfirmDialog from "./ConfirmDialog";

const EMPTY_ROWS = [];
const defaultId = (row) => row.id;
/** One content-table API. renderRow preserves existing expandable/editable MUI Table rows.
 * Server mode requires controlled pagination + rowCount; callbacks never change API contracts.
 * Bulk actions must be explicitly supplied by an authorized caller; danger actions are confirmed.
 */
export default function DataTable({
  columns,
  data,
  rows,
  getRowId = defaultId,
  label = "Records",
  loading = false,
  error,
  onRetry,
  search: controlledSearch,
  onSearch,
  searchable = true,
  searchFields,
  filters,
  onResetFilters,
  onRefresh,
  onExport,
  actions,
  renderRow,
  pagination,
  onPageChange,
  onPageSizeChange,
  rowCount,
  serverSide = false,
  onSort,
  sortModel: controlledSort,
  selection,
  onSelectionChange,
  selectable = false,
  bulkActions = [],
  pageSizeOptions = [5, 10, 20, 50, 100],
  initialPageSize = 10,
  emptyAction,
  ...gridProps
}) {
  const source = data || rows || EMPTY_ROWS;
  const [query, setQuery] = useState("");
  const [localPage, setLocalPage] = useState(0);
  const [localSize, setLocalSize] = useState(initialPageSize);
  const [localSort, setLocalSort] = useState([]);
  const [localSelection, setLocalSelection] = useState([]);
  const [bulkRequest, setBulkRequest] = useState(null);
  const search = controlledSearch ?? query;
  const pageSize = pagination?.pageSize ?? localSize;
  const sortModel = controlledSort ?? localSort;
  const selected = selection ?? localSelection;
  const filtered = useMemo(() => {
    if (serverSide || !search) return source;
    const fields =
      searchFields ||
      columns.filter((col) => col.field !== "actions").map((col) => col.field);
    return source.filter((row) =>
      fields.some((field) =>
        String(row[field] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    );
  }, [source, columns, searchFields, serverSide, search]);
  const count = serverSide ? rowCount ?? 0 : filtered.length;
  const requestedPage = pagination?.page ?? localPage;
  const page = serverSide
    ? requestedPage
    : Math.min(requestedPage, Math.max(0, Math.ceil(count / pageSize) - 1));
  useEffect(() => {
    if (!serverSide && requestedPage !== page) {
      setLocalPage(page);
      onPageChange?.(page);
    }
  }, [serverSide, requestedPage, page, onPageChange]);
  const changePage = (next) => {
    setLocalPage(next);
    onPageChange?.(next);
  };
  const changeSize = (next) => {
    setLocalSize(next);
    onPageSizeChange?.(next);
    changePage(0);
  };
  const changeSearch = (value) => {
    setQuery(value);
    changePage(0);
    onSearch?.(value);
  };
  const changeSort = (model) => {
    setLocalSort(model);
    onSort?.(model);
    changePage(0);
  };
  useEffect(() => {
    if (!serverSide && !loading)
      setLocalSelection((current) =>
        current.filter((id) => source.some((row) => getRowId(row) === id))
      );
  }, [source, getRowId, serverSide, loading]);
  const sorted = useMemo(() => {
    if (serverSide || !renderRow || !sortModel.length) return filtered;
    const { field, sort } = sortModel[0];
    return [...filtered].sort(
      (a, b) =>
        String(a[field] ?? "").localeCompare(
          String(b[field] ?? ""),
          undefined,
          { numeric: true }
        ) * (sort === "desc" ? -1 : 1)
    );
  }, [filtered, sortModel, serverSide, renderRow]);
  const empty = () => (
    <EmptyState
      title={
        search
          ? `No results found for “${search}”`
          : `No ${label.toLowerCase()} yet`
      }
      description={
        search
          ? "Try another search or clear your filters."
          : "Records will appear here when available."
      }
      action={
        search ? (
          <Button onClick={() => changeSearch("")}>Clear search</Button>
        ) : (
          emptyAction
        )
      }
    />
  );
  const runBulk = async (action) => {
    await action.onClick(selected);
    setLocalSelection([]);
    onSelectionChange?.([]);
  };
  return (
    <Paper sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      <FilterBar
        search={search}
        onSearch={searchable ? changeSearch : undefined}
        searchLabel={`Search ${label.toLowerCase()}`}
        onReset={
          onResetFilters
            ? () => {
                changePage(0);
                onResetFilters();
              }
            : undefined
        }
        onRefresh={onRefresh}
        onExport={onExport}
        actions={actions}
        loading={loading}
      >
        {filters}
      </FilterBar>
      {selected.length > 0 && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            bgcolor: "primary.light",
          }}
        >
          <Typography variant="body2">
            {selected.length} records selected
          </Typography>
          {bulkActions
            .filter((action) => action.visible !== false)
            .map((action) => (
              <Button
                key={action.label}
                disabled={loading || action.disabled}
                color={action.danger ? "error" : "primary"}
                onClick={() => setBulkRequest(action)}
              >
                {action.label}
              </Button>
            ))}
        </Box>
      )}
      {error ? (
        <ErrorState onRetry={onRetry || onRefresh} />
      ) : renderRow ? (
        <>
          <TableContainer
            tabIndex={0}
            role="region"
            aria-label={label}
            sx={{ maxHeight: "65vh" }}
          >
            <Table stickyHeader aria-label={label} aria-busy={loading}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      align={
                        col.align ??
                        (/^actions?$/i.test(col.field) ||
                        /^actions?$/i.test(col.headerName)
                          ? "center"
                          : undefined)
                      }
                      sortDirection={
                        sortModel[0]?.field === col.field
                          ? sortModel[0].sort
                          : false
                      }
                    >
                      {col.sortable === false ? (
                        col.headerName
                      ) : (
                        <TableSortLabel
                          active={sortModel[0]?.field === col.field}
                          direction={
                            sortModel[0]?.field === col.field
                              ? sortModel[0].sort
                              : "asc"
                          }
                          onClick={() =>
                            changeSort([
                              {
                                field: col.field,
                                sort:
                                  sortModel[0]?.field === col.field &&
                                  sortModel[0].sort === "asc"
                                    ? "desc"
                                    : "asc",
                              },
                            ])
                          }
                        >
                          {col.headerName}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={columns.length} />
                ) : !sorted.length ? (
                  <TableRow>
                    <TableCell colSpan={columns.length}>{empty()}</TableCell>
                  </TableRow>
                ) : (
                  (serverSide
                    ? sorted
                    : sorted.slice(page * pageSize, (page + 1) * pageSize)
                  ).map((row, index) => (
                    <Fragment key={getRowId(row)}>
                      {renderRow(row, index)}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            count={count}
            page={page}
            pageSize={pageSize}
            onPageChange={changePage}
            onPageSizeChange={changeSize}
            pageSizeOptions={pageSizeOptions}
          />
        </>
      ) : (
        <Box
          sx={{
            height: gridProps.autoHeight ? "auto" : 560,
            width: "100%",
            minWidth: 0,
          }}
        >
          <DataGrid
            {...gridProps}
            aria-label={label}
            rows={filtered}
            columns={columns.map((col) =>
              /^actions?$/i.test(col.field) ||
              /^actions?$/i.test(col.headerName)
                ? {
                    ...col,
                    align: col.align ?? "center",
                    headerAlign: col.headerAlign ?? "center",
                  }
                : col
            )}
            getRowId={getRowId}
            loading={loading}
            disableRowSelectionOnClick
            checkboxSelection={selectable}
            rowSelectionModel={selected}
            onRowSelectionModelChange={(next) => {
              setLocalSelection(next);
              onSelectionChange?.(next);
            }}
            keepNonExistentRowsSelected={serverSide}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              if (model.pageSize !== pageSize) changeSize(model.pageSize);
              else changePage(model.page);
            }}
            pageSizeOptions={[...new Set([...pageSizeOptions, pageSize])]}
            paginationMode={serverSide ? "server" : "client"}
            sortingMode={serverSide ? "server" : "client"}
            filterMode={serverSide ? "server" : "client"}
            rowCount={serverSide ? rowCount : undefined}
            sortModel={sortModel}
            onSortModelChange={changeSort}
            slots={{
              footer: GridPagination,
              noRowsOverlay: empty,
              noResultsOverlay: empty,
              ...gridProps.slots,
            }}
            slotProps={{
              footer: { pageSizeOptions },
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
              ...gridProps.slotProps,
            }}
            sx={{
              ...gridProps.sx,
              border: 0,
              "& .MuiDataGrid-cell": { fontSize: "0.875rem" },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaders": {
                ...tableHeaderStyles,
                fontSize: "0.8rem",
                fontWeight: 700,
              },
            }}
          />
        </Box>
      )}
      <ConfirmDialog
        open={Boolean(bulkRequest)}
        title={`${bulkRequest?.label || "Update"} ${selected.length} records?`}
        description="This action applies to all selected records."
        confirmText={bulkRequest?.label}
        variant={bulkRequest?.danger ? "danger" : "default"}
        onCancel={() => setBulkRequest(null)}
        onConfirm={() => runBulk(bulkRequest)}
      />
    </Paper>
  );
}
